import React, { useState, useEffect } from 'react';
import './FormularioEncuesta.css';

/**
 * Componente de formulario de encuesta
 * Renderiza preguntas según tipo y maneja respuestas
 * @param {Object} props
 * @param {Array} props.preguntas - Array de preguntas
 * @param {Object} props.respuestas - Objeto con respuestas actuales
 * @param {Function} props.onRespuestaChange - Callback cuando cambia una respuesta
 * @param {Function} props.onValidacionChange - Callback cuando cambia la validación
 */
const FormularioEncuesta = ({ preguntas = [], respuestas = {}, onRespuestaChange, onValidacionChange }) => {
  const [respuestasLocales, setRespuestasLocales] = useState(respuestas);
  const [errores, setErrores] = useState({});
  const [preguntasRespondidas, setPreguntasRespondidas] = useState(0);

  // Calcular progreso
  useEffect(() => {
    const respondidas = preguntas.filter(p => {
      const respuesta = respuestasLocales[p.id_pregunta];
      if (!respuesta) return false;
      
      if (p.tipo === 'opcion_multiple') {
        return respuesta.id_opcion != null;
      } else if (p.tipo === 'texto_libre') {
        return respuesta.respuesta_texto && respuesta.respuesta_texto.trim().length > 0;
      }
      return false;
    }).length;

    setPreguntasRespondidas(respondidas);
    
    // Validar todas las preguntas
    const nuevosErrores = {};
    let esValido = true;

    preguntas.forEach(pregunta => {
      const respuesta = respuestasLocales[pregunta.id_pregunta];
      
      if (!respuesta) {
        nuevosErrores[pregunta.id_pregunta] = 'Esta pregunta es obligatoria';
        esValido = false;
      } else if (pregunta.tipo === 'opcion_multiple' && !respuesta.id_opcion) {
        nuevosErrores[pregunta.id_pregunta] = 'Debes seleccionar una opción';
        esValido = false;
      } else if (pregunta.tipo === 'texto_libre' && (!respuesta.respuesta_texto || !respuesta.respuesta_texto.trim())) {
        nuevosErrores[pregunta.id_pregunta] = 'Debes completar este campo';
        esValido = false;
      }
    });

    setErrores(nuevosErrores);
    
    // Notificar al padre sobre el estado de validación
    if (onValidacionChange) {
      onValidacionChange(esValido, respondidas, preguntas.length);
    }
  }, [respuestasLocales, preguntas, onValidacionChange]);

  // Sincronizar con respuestas del padre
  useEffect(() => {
    setRespuestasLocales(respuestas);
  }, [respuestas]);

  const handleRespuestaChange = (idPregunta, tipo, valor) => {
    const nuevaRespuesta = {
      id_pregunta: idPregunta,
      ...(tipo === 'opcion_multiple' 
        ? { id_opcion: valor, respuesta_texto: null }
        : { id_opcion: null, respuesta_texto: valor }
      ),
    };

    const nuevasRespuestas = {
      ...respuestasLocales,
      [idPregunta]: nuevaRespuesta,
    };

    setRespuestasLocales(nuevasRespuestas);
    
    if (onRespuestaChange) {
      onRespuestaChange(nuevasRespuestas);
    }
  };

  const renderPreguntaOpcionMultiple = (pregunta) => {
    const respuesta = respuestasLocales[pregunta.id_pregunta];
    const idOpcionSeleccionada = respuesta?.id_opcion;

    return (
      <div className="pregunta-opciones">
        {pregunta.opciones.map((opcion) => (
          <label key={opcion.id_opcion} className="opcion-radio">
            <input
              type="radio"
              name={`pregunta-${pregunta.id_pregunta}`}
              value={opcion.id_opcion}
              checked={idOpcionSeleccionada === opcion.id_opcion}
              onChange={() => handleRespuestaChange(pregunta.id_pregunta, 'opcion_multiple', opcion.id_opcion)}
            />
            <span className="opcion-texto">{opcion.texto_opcion}</span>
          </label>
        ))}
      </div>
    );
  };

  const renderPreguntaTextoLibre = (pregunta) => {
    const respuesta = respuestasLocales[pregunta.id_pregunta];
    const textoRespuesta = respuesta?.respuesta_texto || '';

    return (
      <div className="pregunta-texto">
        <textarea
          className="texto-libre-input"
          rows="4"
          placeholder="Escribe tu respuesta aquí..."
          value={textoRespuesta}
          onChange={(e) => handleRespuestaChange(pregunta.id_pregunta, 'texto_libre', e.target.value)}
        />
      </div>
    );
  };

  const porcentajeProgreso = preguntas.length > 0 
    ? Math.round((preguntasRespondidas / preguntas.length) * 100)
    : 0;

  return (
    <div className="formulario-encuesta">
      {/* Barra de progreso */}
      <div className="progreso-container">
        <div className="progreso-info">
          <span className="progreso-texto">
            Progreso: {preguntasRespondidas} de {preguntas.length} preguntas
          </span>
          <span className="progreso-porcentaje">{porcentajeProgreso}%</span>
        </div>
        <div className="progreso-bar">
          <div 
            className="progreso-fill" 
            style={{ width: `${porcentajeProgreso}%` }}
          />
        </div>
      </div>

      {/* Lista de preguntas */}
      <div className="preguntas-container">
        {preguntas.map((pregunta, index) => {
          const tieneError = errores[pregunta.id_pregunta];
          const estaRespondida = respuestasLocales[pregunta.id_pregunta] && 
            ((pregunta.tipo === 'opcion_multiple' && respuestasLocales[pregunta.id_pregunta].id_opcion) ||
             (pregunta.tipo === 'texto_libre' && respuestasLocales[pregunta.id_pregunta].respuesta_texto?.trim()));

          return (
            <div 
              key={pregunta.id_pregunta} 
              className={`pregunta-item ${tieneError ? 'pregunta-error' : ''} ${estaRespondida ? 'pregunta-respondida' : ''}`}
            >
              <div className="pregunta-header">
                <span className="pregunta-numero">Pregunta {index + 1}</span>
                {estaRespondida && <span className="pregunta-check">✓</span>}
              </div>
              
              <h3 className="pregunta-texto">{pregunta.texto}</h3>
              
              {pregunta.tipo === 'opcion_multiple' && renderPreguntaOpcionMultiple(pregunta)}
              {pregunta.tipo === 'texto_libre' && renderPreguntaTextoLibre(pregunta)}
              
              {tieneError && (
                <div className="pregunta-error-mensaje">
                  ⚠️ {errores[pregunta.id_pregunta]}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FormularioEncuesta;

